import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, query, getDocs, Timestamp, doc, getDoc, where } from 'firebase/firestore';
import { Clock, User, Check, AlertCircle, Shield } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// Hook personnalisé pour détecter la taille de l'écran
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    
    window.addEventListener("resize", handleResize);
    handleResize();
    
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return windowSize;
}

const PersonalDayView = ({ db, currentDate, currentUser, showCompletedAppointments }) => {
  const { theme } = useTheme();
  
  // Vérifier si currentUser est null et retourner un message d'attente
  if (!currentUser || !currentUser.userId) {
    return (
      <div className="personal-day-view flex flex-col h-full overflow-hidden">
        <div className="container mx-auto px-4 py-3">
          <div className="text-center text-gray-500">
            <p>Chargement des informations utilisateur...</p>
          </div>
        </div>
      </div>
    );
  }

  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [userPermissions, setUserPermissions] = useState({
    read: false,
    write: false,
    create: false,
    delete: false,
    manage: false
  });

  const windowSize = useWindowSize();

  // Fonction pour récupérer les permissions de l'utilisateur
  const fetchUserPermissions = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const userDoc = await getDoc(doc(db, 'employees', currentUser.userId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const userRoleId = userData.role;
        
        // Essayer d'abord le nouveau système advanced_roles
        try {
          const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', userRoleId));
          if (advancedRoleDoc.exists()) {
            const roleData = advancedRoleDoc.data();
            const appointmentsPerms = roleData.permissions?.appointments || {};
            setUserPermissions({
              read: appointmentsPerms.read || false,
              write: appointmentsPerms.write || false,
              create: appointmentsPerms.create || false,
              delete: appointmentsPerms.delete || false,
              manage: appointmentsPerms.manage || false
            });
            return;
          }
        } catch (advancedError) {
          console.log("Nouveau système de permissions non disponible, utilisation du fallback...");
        }
        
        // Fallback: ancien système
        const roleDoc = await getDoc(doc(db, 'roles', userRoleId));
        if (roleDoc.exists()) {
          const roleData = roleDoc.data();
          setUserPermissions({
            read: roleData.permissions.includes('read_appointments'),
            write: roleData.permissions.includes('write_appointments'),
            create: roleData.permissions.includes('write_appointments'),
            delete: roleData.permissions.includes('write_appointments'),
            manage: roleData.permissions.includes('write_appointments')
          });
        }
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des permissions de l'utilisateur:", error);
    }
  }, [db, currentUser]);

  useEffect(() => {
    fetchUserPermissions();
  }, [fetchUserPermissions]);

  useEffect(() => {
    setIsMobile(windowSize.width < 768);
  }, [windowSize]);

  const fetchAppointments = useCallback(async () => {
    if (!db || !currentUser) return;
    try {
      const startOfDay = new Date(currentDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(currentDate);
      endOfDay.setHours(23, 59, 59, 999);
  
      let appointmentsQuery = query(
        collection(db, 'appointments'),
        where('date', '>=', Timestamp.fromDate(startOfDay)),
        where('date', '<=', Timestamp.fromDate(endOfDay)),
        where('employeeId', '==', currentUser.userId)
      );

      const querySnapshot = await getDocs(appointmentsQuery);
      const fetchedAppointments = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        date: doc.data().date?.toDate() || new Date(),
      }));
  
      const appointmentsWithClientInfo = await Promise.all(fetchedAppointments.map(async appointment => {
        const clientDoc = await getDoc(doc(db, 'clients', appointment.clientId));
        const clientData = clientDoc.data();
        return {
          ...appointment,
          clientName: clientData ? `${clientData.lastName} ${clientData.firstName}` : 'Bénéficiaire non spécifié',
          clientColor: clientData ? clientData.color : '#808080',
          status: appointment.status || 'assigne'
        };
      }));

      setAppointments(appointmentsWithClientInfo);
    } catch (error) {
      console.error("Erreur lors de la récupération des rendez-vous:", error);
    }
  }, [db, currentDate, currentUser]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  useEffect(() => {
    const fetchClients = async () => {
      if (!db) return;
      try {
        const clientsRef = collection(db, 'clients');
        const q = query(clientsRef, where('isActive', '==', true));
        const clientsSnapshot = await getDocs(q);
        const fetchedClients = clientsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setClients(fetchedClients);
      } catch (error) {
        console.error("Erreur lors de la récupération des clients:", error);
      }
    };
    fetchClients();
  }, [db]);

  const filteredAppointments = useMemo(() => {
    if (!appointments.length) return [];
    
    return appointments.filter(appointment => {
      const isCompleted = appointment.status === 'completed';
      const shouldShow = showCompletedAppointments || !isCompleted;
      
      return shouldShow;
    }).sort((a, b) => a.startTime.localeCompare(b.startTime));
  }, [appointments, showCompletedAppointments]);

  const renderHeader = () => (
    <div className="personal-dv-header">
      <div className="personal-dv-header-content">
        <div className="flex items-center justify-between w-full">
          <div className="text-lg font-semibold text-gray-800 dark:text-gray-200">
            Mon Planning du Jour
          </div>
          <div className="text-sm text-gray-600 dark:text-gray-400">
            {currentDate.toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              day: 'numeric', 
              month: 'long',
              year: 'numeric'
            })}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAppointment = (appointment) => {
    let appointmentClass = 'personal-pv-appointment';
    let backgroundColor, textColor, borderColor, statusText;

    switch(appointment.status) {
      case 'libre':
        appointmentClass += ' personal-pv-appointment-libre';
        backgroundColor = '#FEE2E2';
        textColor = '#991B1B';
        borderColor = '#EF4444';
        statusText = 'Plage à combler';
        break;
      case 'reserve':
        appointmentClass += ' personal-pv-appointment-reserve';
        backgroundColor = '#FDE68A';
        textColor = '#92400E';
        borderColor = '#FCD34D';
        statusText = 'En attente';
        break;
      case 'in_progress':
        appointmentClass += ' personal-pv-appointment-in-progress';
        backgroundColor = '#FFEDD5';
        textColor = '#9A3412';
        borderColor = '#F97316';
        statusText = 'En cours';
        break;
      case 'completed':
        appointmentClass += ' personal-pv-appointment-completed';
        backgroundColor = 'rgba(156, 163, 175, 0.3)';
        textColor = '#4B5563';
        borderColor = '#9CA3AF';
        statusText = 'Terminé';
        break;
      default:
        appointmentClass += ' personal-pv-appointment-assigne';
        backgroundColor = lightenColor(appointment.clientColor || '#4B5563', 15);
        textColor = isLightColor(backgroundColor) ? '#1F2937' : '#F9FAFB';
        borderColor = appointment.clientColor || '#4B5563';
        statusText = 'Planifié';
    }
  
    return (
      <div
        className={`${appointmentClass} ${isMobile ? 'p-2 mb-2 rounded text-sm' : 'p-3 mb-3 rounded-lg text-sm'} shadow-md relative transition-all duration-200 hover:shadow-lg`}
        style={{ 
          backgroundColor: backgroundColor,
          color: textColor,
          borderLeft: `4px solid ${borderColor}`,
          opacity: appointment.status === 'completed' ? 0.7 : 1
        }}
      >
        <div className={`font-bold truncate ${isMobile ? 'mb-1' : 'mb-2'}`}>
          <User size={isMobile ? 14 : 16} className="inline-block mr-2" />
          {appointment.clientName || "Bénéficiaire non spécifié"}
        </div>
        <div className="bg-white text-gray-800 p-2 rounded">
          <Clock size={isMobile ? 12 : 14} className="inline-block mr-2" />
          {`${appointment.startTime} - ${appointment.endTime}`}
        </div>
        {statusText && (
          <div className={`${isMobile ? 'text-xs' : 'text-sm'} mt-2 font-semibold`}>
            {statusText}
          </div>
        )}
        
        {/* Indicateur de notes si elles existent */}
        {appointment.notes && appointment.notes.content && (
          <div className="notes-indicator mt-2">
            <span className={`${isMobile ? 'text-xs' : 'text-xs'} bg-purple-100 text-purple-800 px-2 py-1 rounded-full flex items-center`}>
              📝 Notes
            </span>
          </div>
        )}
        
        {appointment.status === 'completed' && (
          <div className="absolute top-2 right-2">
            <Check size={isMobile ? 14 : 18} className="text-green-500" />
          </div>
        )}
      </div>
    );
  };

  // Fonction pour éclaircir une couleur
  const lightenColor = (color, amount) => {
    return '#' + color.replace(/^#/, '').replace(/../g, color => ('0' + Math.min(255, Math.max(0, parseInt(color, 16) + amount)).toString(16)).substr(-2));
  };

  // Fonction utilitaire pour déterminer si une couleur est claire ou foncée
  const isLightColor = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return brightness > 155;
  };

  return (
    <div className="personal-dv-container">
      {renderHeader()}
      
      <div className="personal-dv-content">
        {filteredAppointments.length > 0 ? (
          <div className="personal-appointments-grid">
            {filteredAppointments.map(appointment => (
              <div key={appointment.id} className="personal-appointment-item">
                {renderAppointment(appointment)}
              </div>
            ))}
          </div>
        ) : (
          <div className="personal-no-appointments text-center py-8">
            <div className="text-gray-400 mb-4">
              <Clock size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Aucun rendez-vous aujourd'hui</h3>
            <p className="text-gray-500">
              Vous n'avez pas de rendez-vous planifié pour cette journée.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalDayView;
